import { store } from "../store";
import {
  addUpload,
  markUploading,
  setUploadProgress,
  setUploadSuccess,
  setUploadError,
  removeUpload,
} from "./uploadManagerSlice";

// Imperative façade over the uploadManager slice. Pages call runUpload() with
// an async task; the manager owns the lifecycle from there, so the upload (and
// its on-screen progress) outlives the submitting component.
//
// The task function and its success/error callbacks can't live in Redux state
// (not serializable), so they're held here in a module-level registry keyed by
// upload id. The registry is also what makes retry possible — we re-run the
// same stored task.
const registry = new Map();
let seq = 0;

// Success banners auto-clear; error banners stay until the user dismisses them.
const SUCCESS_AUTO_DISMISS_MS = 4000;

async function execute(id) {
  const entry = registry.get(id);
  if (!entry) return;

  store.dispatch(markUploading({ id }));
  const onProgress = (p) =>
    store.dispatch(setUploadProgress({ id, progress: Math.round(p) }));

  try {
    const result = await entry.run(onProgress);
    store.dispatch(setUploadSuccess({ id }));
    if (typeof entry.onSuccess === "function") entry.onSuccess(result);
    setTimeout(() => dismissUpload(id), SUCCESS_AUTO_DISMISS_MS);
  } catch (err) {
    const message =
      err?.message || err?.payload?.message || "Upload failed. Please retry.";
    store.dispatch(setUploadError({ id, error: message }));
    if (typeof entry.onError === "function") entry.onError(err);
    // Entry is intentionally kept in the registry so the banner's Retry can
    // re-run the same task.
  }
}

// Kick off a tracked upload. `run` is `async (onProgress) => result`; it should
// throw on failure. Returns the upload id. Fire-and-forget by design — callers
// should NOT await this if they want the user to be able to navigate away.
export function runUpload({ label, run, onSuccess, onError }) {
  const id = `up_${Date.now()}_${seq++}`;
  registry.set(id, { label, run, onSuccess, onError });
  store.dispatch(addUpload({ id, label }));
  execute(id);
  return id;
}

export function retryUpload(id) {
  if (!registry.has(id)) return;
  execute(id);
}

export function dismissUpload(id) {
  registry.delete(id);
  store.dispatch(removeUpload(id));
}
